import { useCallback, useEffect, useState } from "react"
import type { PostQueriesFromHash200 } from "@web/api/models"
import { usePatchQueriesByIdStatus, usePostQueriesFromHash } from "@web/api/query/query"
import { useGeolocation } from "@web/components/geo-protected-route"

export function useQrVerification(hash: string | undefined) {
	const [state, setState] = useState({
		error: null as string | null,
		is404Error: false,
		isPublicHashModalOpen: false,
		isFakeAlertOpen: false,
		pendingVerification: null as boolean | null,
		publicHashVerificationResult: null as "success" | "error" | null,
		isReportModalOpen: false,
		verificationResult: null as "success" | "error" | null,
		cachedQuery: null as PostQueriesFromHash200 | null,
	})

	const { isGeoError, getCurrentCoordinates } = useGeolocation()

	const { mutate, data, isSuccess, isPending, isError, error: apiError } = usePostQueriesFromHash()
	const { mutate: updateQueryStatus, isLoading: isUpdatingStatus } = usePatchQueriesByIdStatus()

	const updateState = useCallback((updates: Partial<typeof state>) => {
		setState((prev) => ({ ...prev, ...updates }))
	}, [])

	const handleVerification = (matches: boolean, id: string) => {
		if (!data || !id) return
		const status = matches ? "VALID" : "INVALID"
		updateQueryStatus(
			{ id, data: { status } },
			{
				onSuccess: () => updateState({ verificationResult: matches ? "success" : "error" }),
				onError: (err) => {
					console.error("Error updating status:", err)
					updateState({
						error: "Erro ao atualizar o status da verificação. Por favor, tente novamente.",
					})
				},
			},
		)
	}

	const handlePublicHashVerification = (matches: boolean) => {
		const selectId = state?.cachedQuery?.query?.id || data?.query?.id
		const status = matches ? "VALID" : "INVALID"

		if (!selectId) return

		updateState({
			publicHashVerificationResult: matches ? "success" : "error",
		})

		const verificationData = { hash, status }
		const existingVerify = JSON.parse(localStorage.getItem("@queryCachedHasVerify") || "[]")
		const filtered = existingVerify.filter((item: any) => item.hash !== hash)
		const updated = [...filtered, verificationData]
		localStorage.setItem("@queryCachedHasVerify", JSON.stringify(updated))

		if (matches) {
			handleVerification(true, selectId)
			updateState({ isPublicHashModalOpen: false })
		} else {
			handleVerification(false, selectId)
			updateState({
				pendingVerification: false,
				isFakeAlertOpen: true,
				isPublicHashModalOpen: false,
			})
		}
	}

	const handleFakeAlertConfirm = () => {
		const selectId = state?.cachedQuery?.query?.id || data?.query?.id
		if (data && selectId) handleVerification(false, selectId)
		updateState({
			isFakeAlertOpen: false,
			isReportModalOpen: true,
		})
	}

	const handleFakeAlertCancel = () => {
		updateState({
			isFakeAlertOpen: false,
			isPublicHashModalOpen: true,
			pendingVerification: null,
		})
	}

	const execValidationRequest = useCallback(() => {
		if (!hash) {
			updateState({
				error: "Código não encontrado. Verifique o selo e tente novamente.",
			})
			return
		}

		const geo = getCurrentCoordinates?.()
		const coords = geo?.coords

		if (!coords) {
			console.warn("Coordenadas não disponíveis no momento.")
			return
		}

		mutate(
			{
				data: {
					hash,
					latitude: coords.latitude,
					longitude: coords.longitude,
				},
			},
			{
				onSuccess: (responseData) => {
					const newDataCached = { ...responseData, hash, queryId: responseData.query.id }
					const existing = JSON.parse(localStorage.getItem("@queryCached") || "[]")
					const filtered = existing.filter(
						(item: { hash: string; queryId: string }) =>
							item.hash !== hash && item.queryId !== responseData.query.id
					)
					const updatedCached = [...filtered, newDataCached]

					updateState({ cachedQuery: newDataCached })
					localStorage.setItem("@queryCached", JSON.stringify(updatedCached))

					if (responseData?.seal?.publicHash) {
						updateState({ isPublicHashModalOpen: true })
					}
				},
				onError: (err) => {
					console.error("API Error:", err)
					if (err?.response?.status === 404 || err?.status === 404) {
						updateState({
							is404Error: true,
							error: "Selo não encontrado. Verifique se o QR code é válido e tente novamente.",
						})
					} else {
						updateState({
							error: "Erro ao verificar o QR code. Por favor, tente novamente.",
						})
					}
				},
			},
		)
	}, [hash, getCurrentCoordinates, mutate, data, updateState])

	useEffect(() => {
		const localStorageCached = localStorage.getItem("@queryCached")
		const parsed = JSON.parse(localStorageCached as string)

		if (localStorageCached) {
			const match = parsed.find(
				(entry: any) => entry.hash === hash || entry.queryId === hash
			)
			const verifyList = JSON.parse(localStorage.getItem("@queryCachedHasVerify") || "[]")
			const alreadyVerified = verifyList.find((entry: any) => entry.hash === hash)

			if (alreadyVerified && alreadyVerified.status === "VALID") {
				updateState({ publicHashVerificationResult: "success" })
			} else {
				updateState({ publicHashVerificationResult: "error" })
			}

			if (match) {
				if (!alreadyVerified && match?.seal?.publicHash) {
					updateState({ isPublicHashModalOpen: true })
				}
				updateState({ cachedQuery: match })
				return
			}
		}
		const geo = getCurrentCoordinates?.()
		const coords = geo?.coords

		if (hash && (coords || isGeoError)) {
			execValidationRequest()
		}
	}, [hash, execValidationRequest, getCurrentCoordinates, isGeoError, updateState])

	// useEffect(() => {
	// 	if (geoError) {
	// 		updateState({ error: geoError })
	// 	}
	// }, [geoError])

	useEffect(() => {
		if (isError && apiError) {
			console.error("API Error:", apiError)
			if (apiError?.response?.status === 404 || apiError?.status === 404) {
				updateState({
					is404Error: true,
					error: "Selo não encontrado. Verifique se o QR code é válido e tente novamente.",
				})
			} else {
				updateState({
					error: "Erro ao verificar o QR code. Por favor, tente novamente.",
				})
			}
		}
	}, [isError, apiError, updateState])

	useEffect(() => {
		if (isSuccess && data && data.seal && data.seal.publicHash) {
			updateState({ isPublicHashModalOpen: true })
		}
	}, [isSuccess, data, updateState])

	return {
		...state,
		data,
		isPending,
		// isGeoLoading,
		isUpdatingStatus,
		// isGeoError,
		updateState,
		handlePublicHashVerification,
		handleFakeAlertConfirm,
		handleFakeAlertCancel,
		execValidationRequest,
	}
}
